import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Heading,
  Icon,
  PaymentIdentifier,
  PaymentIdentifierLabel,
  PrimaryFullScreenSpinner,
  SelectItem,
  SelectSeparator,
  StatusUI,
  Txt,
  toast,
  uppercaseStatusToStatus,
  useAppForm,
} from "@repo/ui";
import { getCountryLists, NotFoundError } from "@repo/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import countries from "i18n-iso-countries";
import { Suspense } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  CustodyAssetRowFragment,
  SetCustodyAccountAddressInput,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/custody-accounts_/$id")({
  head: () => opsHead("Custody Account"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const assetColumns: ColumnDef<CustodyAssetRowFragment>[] = [
  {
    id: "organisationName",
    accessorFn: (row) => row.businessEntity?.organisation?.name ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Organisation" />
    ),
    cell: ({ row }) => (
      <div className="flex flex-row items-center gap-2">
        <Icon
          imageUrl={
            row.original.businessEntity?.organisation?.squareLogoUrl ??
            undefined
          }
        />
        {row.original.businessEntity?.organisation?.name}
      </div>
    ),
  },
  {
    id: "businessEntityName",
    accessorFn: (row) => row.businessEntity?.name ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Business Entity" />
    ),
    cell: ({ row }) => row.original.businessEntity?.name ?? "-",
  },
  {
    id: "assetStatus",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Asset Status" />
    ),
    cell: ({ row }) => (
      <StatusUI status={uppercaseStatusToStatus[row.original.status]} />
    ),
  },
  {
    id: "assetBalance",
    accessorFn: (row) => row.estimatedValue?.amount,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Asset Balance" />
    ),
    cell: ({ row }) => <CurrencyAmountUI {...row.original.estimatedValue} />,
  },
  {
    id: "assetOpenedAt",
    accessorFn: (row) => row.openedAt,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Asset Opened At" />
    ),
    cell: ({ row }) =>
      row.original.openedAt ? <DateUI date={row.original.openedAt} /> : "-",
  },
];

function View() {
  const { id } = Route.useParams();

  const { data, error } = useSuspenseQuery(
    graphql(`
      query AdminCustodyAccountDetail($id: ID!) {
        node(id: $id) {
          id
          ... on CustodyAccount {
            id
            shortRef
            currency
            product {
              id
              internalName
            }
            address {
              id
              buildingNumber
              streetName
              city
              state
              postcode
              country
            }
            paymentIdentifiers {
              id
              ...PaymentIdentifierComponent
            }
            totalBalance {
              amount
              currency
            }
            assignedBalance {
              amount
              currency
            }
            unassignedBalance {
              amount
              currency
            }
            clearingCustodyAccount {
              id
              shortRef
            }
            assets(includeInactive: true) {
                ...CustodyAssetRow
            }
          }
        }
      }

      fragment CustodyAssetRow on Asset {
        id
        openedAt
        status
        estimatedValue {
            currency
            amount
        }
        businessEntity {
          id
          name
          organisation {
            id
            name
            squareLogoUrl
          }
        }
      }
    `),
    {
      variables: { id },
    },
  );

  if (error) {
    throw error;
  }

  const custodyAccount =
    data.node?.__typename === "CustodyAccount" ? data.node : null;

  if (!custodyAccount) {
    throw new NotFoundError();
  }

  useOpsTitle(`Custody Account #${custodyAccount.shortRef}`);

  const [setAddress, setAddressResult] = useMutation(
    graphql(`
      mutation SetCustodyAccountAddress(
        $input: SetCustodyAccountAddressInput!
      ) {
        setCustodyAccountAddress(input: $input) {
          custodyAccount {
            id
            address {
              id
              buildingNumber
              streetName
              city
              state
              postcode
              country
            }
          }
        }
      }
    `),
    {
      onCompleted: () => {
        toast({
          kind: "success",
          title: "Custody account address updated",
        });
      },
      onError: (error) => {
        const errorMessage =
          error.graphQLErrors?.[0]?.message || "Unexpected error";
        toast({
          kind: "error",
          title: "Error setting address",
          description: errorMessage,
        });
      },
    },
  );

  const { commonCountriesList, uncommonCountriesList } = getCountryLists();

  const addressForm = useAppForm({
    defaultValues: {
      address: {
        buildingNumber: custodyAccount.address?.buildingNumber ?? "",
        streetName: custodyAccount.address?.streetName ?? "",
        city: custodyAccount.address?.city ?? "",
        state: custodyAccount.address?.state ?? "",
        postcode: custodyAccount.address?.postcode ?? "",
        country:
          [...commonCountriesList, ...uncommonCountriesList].find(
            ({ label }) => label === custodyAccount.address?.country,
          )?.value ?? "AUS",
      },
    } as Omit<SetCustodyAccountAddressInput, "custodyAccountId">,
    validators: {
      onSubmit: z.object({
        address: z.object({
          buildingNumber: z.string(),
          streetName: z.string(),
          city: z.string(),
          state: z.string(),
          postcode: z.string(),
          country: z.string().refine(countries.isValid),
        }),
      }),
    },
    onSubmit: async ({ value: formFields }) => {
      setAddress({
        variables: {
          input: {
            custodyAccountId: custodyAccount.id,
            address: formFields.address,
          },
        },
      });
    },
  });

  const navigate = useNavigate();

  const assets = [...(custodyAccount.assets ?? [])];
  const paymentIdentifiers = custodyAccount.paymentIdentifiers ?? [];

  return (
    <AdminPageContainer testId="admin-custody-account-detail">
      <AdminPageHeader
        title={`Custody Account #${custodyAccount.shortRef}`}
        subtitle={custodyAccount.id}
        buttons={
          <Link to="/custody-accounts">
            <Button variant="outline" size="sm">
              Back to list
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>
              <Heading size="2xl" as="h2">
                Details
              </Heading>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-row justify-between gap-2">
              <Txt size="sm" className="text-zinc-500">
                Product
              </Txt>
              <Txt className="text-right text-sm">
                {custodyAccount.product?.internalName ?? "-"}
              </Txt>
            </div>
            <div className="flex flex-row justify-between gap-2">
              <Txt size="sm" className="text-zinc-500">
                Currency
              </Txt>
              <Txt className="text-right text-sm">
                {custodyAccount.currency}
              </Txt>
            </div>
            <div className="flex flex-row justify-between gap-2">
              <Txt size="sm" className="text-zinc-500">
                Total Balance
              </Txt>
              <CurrencyAmountUI {...custodyAccount.totalBalance} />
            </div>
            <div className="flex flex-row justify-between gap-2">
              <Txt size="sm" className="text-zinc-500">
                Assigned Balance
              </Txt>
              <CurrencyAmountUI {...custodyAccount.assignedBalance} />
            </div>
            <div className="flex flex-row justify-between gap-2">
              <Txt size="sm" className="text-zinc-500">
                Unassigned Balance
              </Txt>
              <CurrencyAmountUI {...custodyAccount.unassignedBalance} />
            </div>
            {custodyAccount.clearingCustodyAccount && (
              <div className="flex flex-row justify-between gap-2">
                <Txt size="sm" className="text-zinc-500">
                  Clearing Account
                </Txt>
                <Link
                  to="/custody-accounts/$id"
                  params={{ id: custodyAccount.clearingCustodyAccount.id }}
                  className="text-right text-blue-600 text-sm hover:underline"
                >
                  #{custodyAccount.clearingCustodyAccount.shortRef}
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <Heading size="2xl" as="h2">
                Payment Identifiers
              </Heading>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {paymentIdentifiers.length === 0 ? (
              <Txt className="text-zinc-500">No payment identifiers</Txt>
            ) : (
              paymentIdentifiers.map((paymentIdentifier) => (
                <Card key={paymentIdentifier.id}>
                  <CardHeader>
                    <CardTitle>
                      <Heading size="xl" as="h3">
                        <PaymentIdentifierLabel
                          paymentIdentifier={paymentIdentifier}
                        />
                      </Heading>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PaymentIdentifier paymentIdentifier={paymentIdentifier} />
                  </CardContent>
                </Card>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>
            <Heading size="2xl" as="h2">
              Address
            </Heading>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <addressForm.AppForm>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void addressForm.handleSubmit();
              }}
            >
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-3 gap-4">
                  <addressForm.AppField name="address.buildingNumber">
                    {(field) => <field.InputField label="Building number" />}
                  </addressForm.AppField>
                  <addressForm.AppField name="address.streetName">
                    {(field) => <field.InputField label="Street name" />}
                  </addressForm.AppField>
                  <addressForm.AppField name="address.city">
                    {(field) => <field.InputField label="City" />}
                  </addressForm.AppField>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <addressForm.AppField name="address.state">
                    {(field) => <field.InputField label="State" />}
                  </addressForm.AppField>
                  <addressForm.AppField name="address.postcode">
                    {(field) => <field.InputField label="Postcode" />}
                  </addressForm.AppField>
                  <addressForm.AppField name="address.country">
                    {(field) => (
                      <field.SelectField
                        label="Country"
                        placeholder="Select a country"
                      >
                        {commonCountriesList.map(({ value, label }) => (
                          <SelectItem value={value} key={`common-${value}`}>
                            {label}
                          </SelectItem>
                        ))}
                        <SelectSeparator />
                        {uncommonCountriesList.map(({ value, label }) => (
                          <SelectItem value={value} key={`uncommon-${value}`}>
                            {label}
                          </SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </addressForm.AppField>
                </div>

                <div className="flex justify-end">
                  <Button disabled={setAddressResult.loading}>Save</Button>
                </div>
              </div>
            </form>
          </addressForm.AppForm>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>
            <Heading size="2xl" as="h2">
              Assets
            </Heading>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {assets.length === 0 ? (
            <Txt className="text-zinc-500">
              No assets in this custody account
            </Txt>
          ) : (
            <DataTable
              data={assets}
              columns={assetColumns}
              className="overflow-hidden border border-zinc-200"
              onRowClick={(row) => {
                navigate({
                  to: "/assets/$id",
                  params: { id: row.original.id },
                });
              }}
            />
          )}
        </CardContent>
      </Card>
    </AdminPageContainer>
  );
}
